import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import html2canvas from 'html2canvas';
import styles from './Dashboard.module.css';
import AppSidebar from '../../components/AppSidebar/AppSidebar';
import dashboardApi from '../../api/dashboardApi';

// ─── Iconos ────────────────────────────────────────────────────────────────
function BuildingIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}

function fileNameFromTitle(title) {
  return title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function DownloadChartButton({ title }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (event) => {
    const chartCard = event.currentTarget.closest(`.${styles.chartCard}`);
    if (!chartCard || downloading) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(chartCard, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (element) => element.hasAttribute('data-download-control'),
      });
      const link = document.createElement('a');
      link.download = `${fileNameFromTitle(title) || 'grafica'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('No se pudo descargar la gráfica:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      className={styles.downloadChartButton}
      onClick={handleDownload}
      disabled={downloading}
      data-download-control
      aria-label={`Descargar ${title} en PNG`}
      title="Descargar gráfica en PNG"
    >
      <DownloadIcon />
      <span>{downloading ? 'Generando…' : 'PNG'}</span>
    </button>
  );
}

// ─── Colores ────────────────────────────────────────────────────────────────
const METABOLIC_COLORS  = ['#00be9c', '#ffe951', '#dc3545'];
// IMC: Normal=verde · Sobrepeso=amarillo · Obesidad I=naranja claro · II=naranja · III=rojo
const IMC_COLORS        = ['#00be9c', '#ffe951', '#ffa040', '#fd7e14', '#dc3545'];
const DISEASE_COLORS    = ['#dc3545', '#e06c75', '#e88f99', '#f0b4bb', '#f8d7da'];
// InBody PBF (Grasa Corporal): Bajo=amarillo · Normal=verde · Alto=rojo
const INBODY_PBF_COLORS = ['#ffe951', '#00be9c', '#dc3545'];
// InBody VFL (Grasa Visceral): Normal=verde · Alto=amarillo · Muy alto=rojo
const INBODY_VFL_COLORS = ['#00be9c', '#ffe951', '#dc3545'];
// Lab: 3 categorías riesgo — verde/amarillo/rojo
const LAB_RISK_COLORS   = ['#00be9c', '#ffe951', '#dc3545'];
// HDL (mayor es mejor): Bajo=rojo · Moderado=amarillo · Óptimo=verde
const LAB_HDL_COLORS    = ['#dc3545', '#ffe951', '#00be9c'];
// LDL: 4 categorías — verde/azul/amarillo/rojo
const LAB_LDL_COLORS    = ['#00be9c', '#1069f1', '#ffe951', '#dc3545'];
// TyG: Normal=verde · Límite=amarillo · Elevado=rojo
const TYG_COLORS        = ['#00be9c', '#ffe951', '#dc3545'];

// ─── Label personalizado para gráficas de pastel ───────────────────────────
// Muestra el valor DENTRO del arco cuando el segmento es suficientemente grande,
// o FUERA con una línea punteada cuando es pequeño.
const RADIAN = Math.PI / 180;
const SMALL_SEGMENT_THRESHOLD = 0.07; // < 7% se considera pequeño

function PieLabel({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }) {
  if (!value || value === 0) return null;

  const isSmall = percent < SMALL_SEGMENT_THRESHOLD;

  if (!isSmall) {
    // ── Label DENTRO del arco ──────────────────────────────────────────────
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x} y={y}
        fill="#042330"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fontWeight={700}
        fontFamily="Montserrat, sans-serif"
      >
        {value}
      </text>
    );
  }

  // ── Label FUERA con línea punteada ─────────────────────────────────────
  const startR  = outerRadius + 6;
  const endR    = outerRadius + 28;
  const x1 = cx + startR * Math.cos(-midAngle * RADIAN);
  const y1 = cy + startR * Math.sin(-midAngle * RADIAN);
  const x2 = cx + endR   * Math.cos(-midAngle * RADIAN);
  const y2 = cy + endR   * Math.sin(-midAngle * RADIAN);

  // Pequeño tick horizontal al final de la línea
  const isRight  = x2 > cx;
  const tickEndX = x2 + (isRight ? 8 : -8);

  return (
    <g>
      {/* Línea punteada radial */}
      <line
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke="#9aabba"
        strokeWidth={1}
        strokeDasharray="3 2"
      />
      {/* Tick horizontal */}
      <line
        x1={x2} y1={y2} x2={tickEndX} y2={y2}
        stroke="#9aabba"
        strokeWidth={1}
      />
      {/* Número */}
      <text
        x={tickEndX + (isRight ? 3 : -3)}
        y={y2}
        fill="#042330"
        textAnchor={isRight ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={10}
        fontWeight={700}
        fontFamily="Montserrat, sans-serif"
      >
        {value}
      </text>
    </g>
  );
}

// ─── Stacked bar: configuración de indicadores ────────────────────────────
const BAR_INDICATORS = [
  { dataKey: 'glucosa',          label: 'Glucosa',       names: ['Normal', 'Prediabetes', 'Diabetes'] },
  { dataKey: 'trigliceridos',    label: 'Triglicéridos', names: ['Normal', 'Límite alto', 'Alto'] },
  { dataKey: 'colesterol_total', label: 'Colesterol',    names: ['Deseable', 'Límite alto', 'Alto'] },
  { dataKey: 'hba1c',            label: 'HbA1c',         names: ['Normal', 'Prediabetes', 'Diabetes'] },
];
const STACK_COLORS = ['#00be9c', '#ffe951', '#dc3545'];
const TIER_KEYS    = ['tier1', 'tier2', 'tier3'];
const INDICATOR_NAMES = Object.fromEntries(BAR_INDICATORS.map(({ label, names }) => [label, names]));

function buildBarData(labData) {
  if (!labData) return null;
  const pick = (arr, idx) => arr?.[idx]?.value ?? 0;
  return BAR_INDICATORS.map(({ dataKey, label }) => {
    const r1 = pick(labData[dataKey], 0);
    const r2 = pick(labData[dataKey], 1);
    const r3 = pick(labData[dataKey], 2);
    const total = r1 + r2 + r3;
    const pct = v => total > 0 ? (v / total) * 100 : 0;
    return {
      label,
      count1: r1, count2: r2, count3: r3,
      tier1: pct(r1), tier2: pct(r2), tier3: pct(r3),
    };
  });
}

// ─── Stacked bar secundario: HDL, LDL, Índ. Aterogénico, Índ. TyG ─────────
const SECONDARY_INDICATORS = [
  { labKey: 'hdl',               label: 'HDL',              names: ['Bajo','Moderado','Óptimo'],                    colors: ['#dc3545','#ffe951','#00be9c', null] },
  { labKey: 'ldl',               label: 'LDL',              names: ['Óptimo','Casi óptimo','Límite alto','Alto'],    colors: ['#00be9c','#1069f1','#ffe951','#dc3545'] },
  { labKey: 'indice_aterogenico',label: 'Índ. Aterogénico', names: ['Bajo riesgo','Riesgo moderado','Alto riesgo'], colors: ['#00be9c','#ffe951','#dc3545', null] },
  { labKey: 'tyg',               label: 'Índ. TyG',         names: ['Normal','Límite','Elevado'],                   colors: ['#00be9c','#ffe951','#dc3545', null] },
];
const TIER_KEYS4 = ['tier1', 'tier2', 'tier3', 'tier4'];

function buildSecondaryBarData(labData, tygData) {
  const pick = (arr, idx) => arr?.[idx]?.value ?? 0;
  return SECONDARY_INDICATORS.map(({ labKey, label, names, colors }) => {
    const src = labKey === 'tyg' ? tygData : labData?.[labKey];
    const r1 = pick(src, 0);
    const r2 = pick(src, 1);
    const r3 = pick(src, 2);
    const r4 = labKey === 'ldl' ? pick(src, 3) : 0;
    const total = r1 + r2 + r3 + r4;
    const pct = v => total > 0 ? (v / total) * 100 : 0;
    return {
      label, names, colors,
      count1: r1, count2: r2, count3: r3, count4: r4,
      tier1: pct(r1), tier2: pct(r2), tier3: pct(r3), tier4: pct(r4),
    };
  });
}

const MIN_LABEL_HEIGHT = 22;

function makeBarShape(fill, dataKey) {
  return function BarShape(props) {
    const { x, y, width, height } = props;
    const value = props[dataKey]; // valor real del segmento, no el acumulado
    const rect = <rect x={x} y={y} width={width} height={Math.max(height, 0)} fill={fill} />;
    if (!value || value <= 0 || height <= 0) return rect;
    const label = `${Math.round(value)}%`;
    const cx = x + width / 2;
    const cy = y + height / 2;
    if (height >= MIN_LABEL_HEIGHT) {
      return (
        <g>
          {rect}
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
            fontSize={11} fontWeight={700} fill="#042330" fontFamily="Montserrat, sans-serif">
            {label}
          </text>
        </g>
      );
    }
    const lineX1 = x + width + 2;
    const lineX2 = x + width + 20;
    return (
      <g>
        {rect}
        <line x1={lineX1} y1={cy} x2={lineX2} y2={cy}
          stroke="#9aabba" strokeWidth={1} strokeDasharray="3 2" />
        <text x={lineX2 + 3} y={cy} textAnchor="start" dominantBaseline="central"
          fontSize={10} fontWeight={700} fill="#042330" fontFamily="Montserrat, sans-serif">
          {label}
        </text>
      </g>
    );
  };
}

// ─── Bar shape secundario: color viene del array colors[] de la fila ─────────
function makeBarShape2(dataKey, tierIdx) {
  return function BarShape(props) {
    const { x, y, width, height } = props;
    const fill  = props.colors?.[tierIdx] ?? 'transparent';
    const value = props[dataKey];
    const rect  = <rect x={x} y={y} width={width} height={Math.max(height, 0)} fill={fill} />;
    if (!value || value <= 0 || height <= 0) return rect;
    const label = `${Math.round(value)}%`;
    const cx = x + width / 2;
    const cy = y + height / 2;
    if (height >= MIN_LABEL_HEIGHT) {
      return (
        <g>
          {rect}
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
            fontSize={11} fontWeight={700} fill="#042330" fontFamily="Montserrat, sans-serif">
            {label}
          </text>
        </g>
      );
    }
    const lineX1 = x + width + 2;
    const lineX2 = x + width + 20;
    return (
      <g>
        {rect}
        <line x1={lineX1} y1={cy} x2={lineX2} y2={cy}
          stroke="#9aabba" strokeWidth={1} strokeDasharray="3 2" />
        <text x={lineX2 + 3} y={cy} textAnchor="start" dominantBaseline="central"
          fontSize={10} fontWeight={700} fill="#042330" fontFamily="Montserrat, sans-serif">
          {label}
        </text>
      </g>
    );
  };
}

function SecondaryTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{label}</span>
      {TIER_KEYS4.map((key, i) => {
        const count = row[`count${i + 1}`];
        const name  = row.names?.[i];
        if (!count || !name) return null;
        return (
          <span key={key} style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: row.colors?.[i] ?? '#042330' }}>
            {name}: {count}
          </span>
        );
      })}
    </div>
  );
}

function StackedSecondaryChart({ labData, tygData, loading }) {
  const data    = buildSecondaryBarData(labData, tygData);
  const hasData = data?.some(d => d.count1 + d.count2 + d.count3 + d.count4 > 0);

  return (
    <div className={styles.chartCard} style={{ marginTop: '1rem' }}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>Lípidos e Índices Metabólicos</h3>
        {!loading && hasData && (
          <span className={styles.chartTotal}>
            {data[0].count1 + data[0].count2 + data[0].count3 + data[0].count4}
          </span>
        )}
        {!loading && hasData && <DownloadChartButton title="Lípidos e Índices Metabólicos" />}
      </div>
      {loading ? (
        <div className={styles.chartSkeleton} />
      ) : !hasData ? (
        <div className={styles.chartEmpty}>Sin datos disponibles</div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} barSize={80} margin={{ top: 16, right: 48, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8edf2" />
            <XAxis dataKey="label"
              tick={{ fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat, sans-serif', fill: '#042330' }}
              axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`}
              tick={{ fontSize: 10, fill: '#76797c', fontFamily: 'Montserrat, sans-serif' }}
              axisLine={false} tickLine={false} width={36} />
            <Tooltip content={<SecondaryTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            {TIER_KEYS4.map((key, i) => (
              <Bar key={key} dataKey={key} stackId="a"
                radius={i === TIER_KEYS4.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                shape={makeBarShape2(key, i)} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function BarChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const names = INDICATOR_NAMES[label] ?? ['Normal', 'Límite', 'Alto'];
  // recharts entrega tier3→tier1 (Alto→Normal), igual que la barra de arriba a abajo
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{label}</span>
      {[...payload].reverse().map((p) => {
        const tierIndex = TIER_KEYS.indexOf(p.dataKey);
        const count = p.payload[`count${tierIndex + 1}`];
        if (!count) return null;
        return (
          <span key={p.dataKey} style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: STACK_COLORS[tierIndex] }}>
            {names[tierIndex]}: {count}
          </span>
        );
      })}
    </div>
  );
}

function StackedLabChart({ labData, loading }) {
  const data    = buildBarData(labData);
  const hasData = data?.some(d => d.count1 + d.count2 + d.count3 > 0);

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>Indicadores Metabólicos</h3>
        {!loading && hasData && (
          <span className={styles.chartTotal}>
            {data[0].count1 + data[0].count2 + data[0].count3}
          </span>
        )}
        {!loading && hasData && <DownloadChartButton title="Indicadores Metabólicos" />}
      </div>
      {loading ? (
        <div className={styles.chartSkeleton} />
      ) : !hasData ? (
        <div className={styles.chartEmpty}>Sin datos disponibles</div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} barSize={80} margin={{ top: 16, right: 48, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8edf2" />
            <XAxis dataKey="label"
              tick={{ fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat, sans-serif', fill: '#042330' }}
              axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`}
              tick={{ fontSize: 10, fill: '#76797c', fontFamily: 'Montserrat, sans-serif' }}
              axisLine={false} tickLine={false} width={36} />
            <Tooltip content={<BarChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Legend iconType="circle" iconSize={8}
              wrapperStyle={{ fontSize: '0.7rem', paddingTop: '8px' }}
              payload={[
                { value: 'Normal / Deseable',    type: 'circle', color: STACK_COLORS[0] },
                { value: 'Límite / Prediabetes', type: 'circle', color: STACK_COLORS[1] },
                { value: 'Alto / Diabetes',      type: 'circle', color: STACK_COLORS[2] },
              ]} />
            {TIER_KEYS.map((key, i) => (
              <Bar key={key} dataKey={key} stackId="a"
                radius={i === TIER_KEYS.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                shape={makeBarShape(STACK_COLORS[i], key)} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Tooltip personalizado ─────────────────────────────────────────────────
function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{name}</span>
      <span className={styles.tooltipValue}>{value} respuestas</span>
    </div>
  );
}

// ─── Tarjeta de stat ───────────────────────────────────────────────────────
function StatCard({ icon, value, label, loading }) {
  return (
    <div className={styles.card}>
      <div className={styles.cardIcon}>{icon}</div>
      <div className={styles.cardValue}>
        {loading ? <span className={styles.skeleton} /> : value}
      </div>
      <div className={styles.cardLabel}>{label}</div>
    </div>
  );
}

// ─── Gráfica de pastel ─────────────────────────────────────────────────────
function DonutChart({ title, data, colors, loading, startAngle = 0 }) {
  const hasData = data?.some(d => d.value > 0);
  const total = data?.reduce((sum, d) => sum + (d.value || 0), 0) ?? 0;

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>{title}</h3>
        {!loading && hasData && <span className={styles.chartTotal}>{total}</span>}
        {!loading && hasData && <DownloadChartButton title={title} />}
      </div>
      {loading ? (
        <div className={styles.chartSkeleton} />
      ) : !hasData ? (
        <div className={styles.chartEmpty}>Sin datos disponibles</div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <Pie
              data={data}
              cx="50%"
              cy="45%"
              innerRadius={52}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
              startAngle={startAngle}
              endAngle={startAngle + 360}
              label={PieLabel}
              labelLine={false}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '0.7rem', paddingTop: '6px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Riesgo Metabólico Real (donut con porcentajes) ───────────────────────
const MR_COLORS = ['#00be9c', '#ffe951', '#dc3545'];

function PiePctLabel({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }) {
  if (!value) return null;
  const display = `${Math.round(percent * 100)}%`;
  if (percent >= 0.07) {
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    return (
      <text x={cx + r * Math.cos(-midAngle * RADIAN)} y={cy + r * Math.sin(-midAngle * RADIAN)}
        fill="#042330" textAnchor="middle" dominantBaseline="central"
        fontSize={11} fontWeight={700} fontFamily="Montserrat, sans-serif">
        {display}
      </text>
    );
  }
  const startR = outerRadius + 6, endR = outerRadius + 28;
  const x1 = cx + startR * Math.cos(-midAngle * RADIAN), y1 = cy + startR * Math.sin(-midAngle * RADIAN);
  const x2 = cx + endR   * Math.cos(-midAngle * RADIAN), y2 = cy + endR   * Math.sin(-midAngle * RADIAN);
  const isRight = x2 > cx, tickEndX = x2 + (isRight ? 8 : -8);
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#9aabba" strokeWidth={1} strokeDasharray="3 2" />
      <line x1={x2} y1={y2} x2={tickEndX} y2={y2} stroke="#9aabba" strokeWidth={1} />
      <text x={tickEndX + (isRight ? 3 : -3)} y={y2} fill="#042330"
        textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central"
        fontSize={10} fontWeight={700} fontFamily="Montserrat, sans-serif">{display}</text>
    </g>
  );
}

function MrTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{payload[0].name}</span>
      <span className={styles.tooltipValue}>{payload[0].value} personas</span>
    </div>
  );
}

function MetabolicRiskDonutChart({ data, loading }) {
  const hasData = data?.some(d => d.value > 0);
  const total   = data?.reduce((sum, d) => sum + (d.value || 0), 0) ?? 0;
  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>Riesgo Metabólico (cruce InBody × QS)</h3>
        {!loading && hasData && <span className={styles.chartTotal}>{total}</span>}
        {!loading && hasData && <DownloadChartButton title="Riesgo Metabólico cruce InBody y QS" />}
      </div>
      {loading ? <div className={styles.chartSkeleton} /> :
       !hasData ? <div className={styles.chartEmpty}>Sin datos disponibles</div> : (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <Pie data={data} cx="50%" cy="45%" innerRadius={52} outerRadius={80}
              paddingAngle={2} dataKey="value" label={PiePctLabel} labelLine={false}>
              {data.map((_, i) => <Cell key={i} fill={MR_COLORS[i % MR_COLORS.length]} />)}
            </Pie>
            <Tooltip content={<MrTooltip />} />
            <Legend iconType="circle" iconSize={8}
              wrapperStyle={{ fontSize: '0.7rem', paddingTop: '6px' }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Top enfermedades ──────────────────────────────────────────────────────
function TopDiseases({ data, loading }) {
  const max = data?.[0]?.count || 1;

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>Top Riesgos Globales</h3>
        {!loading && data?.length > 0 && <DownloadChartButton title="Top Riesgos Globales" />}
      </div>
      {loading ? (
        <div className={styles.chartSkeleton} />
      ) : !data?.length ? (
        <div className={styles.chartEmpty}>Sin datos disponibles</div>
      ) : (
        <ul className={styles.diseaseList}>
          {data.map((d, i) => (
            <li key={d.name} className={styles.diseaseItem}>
              <div className={styles.diseaseHeader}>
                <span className={styles.diseaseDot} style={{ background: DISEASE_COLORS[i] }} />
                <span className={styles.diseaseName} title={d.name}>{d.name}</span>
                <span className={styles.diseaseCount}>{d.count}</span>
              </div>
              <div className={styles.diseaseBar}>
                <div
                  className={styles.diseaseBarFill}
                  style={{
                    width: `${Math.round((d.count / max) * 100)}%`,
                    background: DISEASE_COLORS[i],
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const MR_MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
function crossKey(c) { return `${c.ibPeriod.month}-${c.ibPeriod.year}-${c.labPeriod.month}-${c.labPeriod.year}`; }
function crossLabel(c) { return `InBody ${MR_MONTH_NAMES[c.ibPeriod.month-1]} ${c.ibPeriod.year} × QS ${MR_MONTH_NAMES[c.labPeriod.month-1]} ${c.labPeriod.year}`; }

// ─── Página principal ──────────────────────────────────────────────────────
export default function Dashboard() {
  const [stats,     setStats]     = useState(null);
  const [metabolic, setMetabolic] = useState([]);
  const [imc,       setImc]       = useState([]);
  const [diseases,  setDiseases]  = useState([]);
  const [inbodyBmi, setInbodyBmi] = useState([]);
  const [inbodyPbf, setInbodyPbf] = useState([]);
  const [inbodyVfl, setInbodyVfl] = useState([]);
  const [labData,     setLabData]     = useState(null);
  const [tygData,     setTygData]     = useState(null);
  const [mrData,      setMrData]      = useState(null);
  const [loadingStats,  setLoadingStats]  = useState(true);
  const [loadingHra,    setLoadingHra]    = useState(true);
  const [loadingInbody, setLoadingInbody] = useState(true);
  const [loadingLab,    setLoadingLab]    = useState(true);
  const [loadingMr,     setLoadingMr]     = useState(false);

  // Riesgo Metabólico Real: selección de empresa y cruce
  const [companiesWithCrosses,   setCompaniesWithCrosses]   = useState([]);
  const [selectedMrCompany,      setSelectedMrCompany]      = useState('');
  const [crossesForMrCompany,    setCrossesForMrCompany]    = useState([]);
  const [selectedMrCross,        setSelectedMrCross]        = useState(null);

  useEffect(() => {
    dashboardApi.getGeneralStats()
      .then(setStats)
      .catch(err => console.error('Stats:', err))
      .finally(() => setLoadingStats(false));

    Promise.all([
      dashboardApi.getHraMetabolicRisk(),
      dashboardApi.getHraImc(),
      dashboardApi.getHraTopDiseases(),
    ])
      .then(([m, i, d]) => { setMetabolic(m); setImc(i); setDiseases(d); })
      .catch(err => console.error('HRA analytics:', err))
      .finally(() => setLoadingHra(false));

    Promise.all([
      dashboardApi.getInbodyBmi(),
      dashboardApi.getInbodyPbf(),
      dashboardApi.getInbodyVfl(),
    ])
      .then(([bmi, pbf, vfl]) => { setInbodyBmi(bmi); setInbodyPbf(pbf); setInbodyVfl(vfl); })
      .catch(err => console.error('InBody analytics:', err))
      .finally(() => setLoadingInbody(false));

    dashboardApi.getLabAnalytics()
      .then(setLabData)
      .catch(err => console.error('Lab analytics:', err))
      .finally(() => setLoadingLab(false));

    dashboardApi.getTygAnalytics()
      .then(setTygData)
      .catch(err => console.error('TyG analytics:', err));

    // Cargar empresas con cruces para el selector
    dashboardApi.getCompaniesWithCrosses()
      .then(setCompaniesWithCrosses)
      .catch(err => console.error('Companies with crosses:', err));
  }, []);

  // Cargar cruces cuando cambia la empresa seleccionada
  useEffect(() => {
    setCrossesForMrCompany([]);
    setSelectedMrCross(null);
    setMrData(null);
    if (!selectedMrCompany) return;
    dashboardApi.getAvailableCrossesForCompany(selectedMrCompany)
      .then(setCrossesForMrCompany)
      .catch(err => console.error('Crosses for company:', err));
  }, [selectedMrCompany]);

  // Cargar datos de riesgo metabólico cuando se selecciona un cruce
  useEffect(() => {
    if (!selectedMrCompany || !selectedMrCross) { setMrData(null); return; }
    setLoadingMr(true);
    dashboardApi.getMetabolicRiskReal(selectedMrCompany, selectedMrCross)
      .then(setMrData)
      .catch(err => console.error('Metabolic risk real:', err))
      .finally(() => setLoadingMr(false));
  }, [selectedMrCompany, selectedMrCross]);

  return (
    <div className={styles.container}>
      <AppSidebar />

      <div className={styles.main}>
        {/* Cabecera */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Panel Admin Deli Life</h1>
          <p className={styles.pageSubtitle}>Analítica global · Información general</p>
        </div>

        {/* ── Información General ─────────────────────────────────────── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Información General</h2>
          <div className={styles.cardGrid}>
            <StatCard icon={<BuildingIcon />} value={stats?.companiesCount ?? '—'} label="Empresas activas" loading={loadingStats} />
            <StatCard icon={<UsersIcon />}    value={stats?.employeesCount  ?? '—'} label="Usuarios totales"  loading={loadingStats} />
          </div>
        </section>

        {/* ── Global HRA ──────────────────────────────────────────────── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Global Health Risk Assessment</h2>
          <div className={styles.hraGrid}>
            <DonutChart title="Riesgo Metabólico Global" data={metabolic} colors={METABOLIC_COLORS} loading={loadingHra} />
            <DonutChart title="IMC"                      data={imc}       colors={IMC_COLORS}       loading={loadingHra} startAngle={90} />
            <TopDiseases data={diseases} loading={loadingHra} />
          </div>
        </section>
        {/* ── InBody ──────────────────────────────────────────────── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>InBody</h2>
          <div className={styles.hraGrid}>
            <DonutChart title="IMC (InBody)" data={inbodyBmi} colors={IMC_COLORS} loading={loadingInbody} />
            <DonutChart title="Grasa Corporal (PBF)" data={inbodyPbf} colors={INBODY_PBF_COLORS} loading={loadingInbody} />
            <DonutChart title="Grasa Visceral (VFL)" data={inbodyVfl} colors={INBODY_VFL_COLORS} loading={loadingInbody} />
          </div>
        </section>

        {/* ── Resultados de Laboratorio ────────────────────────────── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Resultados de Laboratorio</h2>
          <StackedLabChart labData={labData} loading={loadingLab} />
          <StackedSecondaryChart labData={labData} tygData={tygData} loading={loadingLab} />
        </section>

        {/* ── Riesgo Metabólico Real ───────────────────────────────── */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Riesgo Metabólico Real</h2>
            <div className={styles.mrSelectors}>
              <select
                className={styles.mrSelect}
                value={selectedMrCompany}
                onChange={e => setSelectedMrCompany(e.target.value)}
              >
                <option value="">Seleccionar empresa…</option>
                {companiesWithCrosses.map(c => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
              {crossesForMrCompany.length > 0 && (
                <select
                  className={styles.mrSelect}
                  value={selectedMrCross ? crossKey(selectedMrCross) : ''}
                  onChange={e => {
                    if (!e.target.value) { setSelectedMrCross(null); return; }
                    const found = crossesForMrCompany.find(c => crossKey(c) === e.target.value);
                    setSelectedMrCross(found || null);
                  }}
                >
                  <option value="">Seleccionar cruce…</option>
                  {crossesForMrCompany.map(c => (
                    <option key={crossKey(c)} value={crossKey(c)}>{crossLabel(c)}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            {!selectedMrCompany ? (
              <div className={styles.mrPrompt}>Selecciona una empresa para ver los cruces disponibles.</div>
            ) : !selectedMrCross ? (
              <div className={styles.mrPrompt}>Selecciona un cruce para ver el riesgo metabólico.</div>
            ) : (
              <MetabolicRiskDonutChart
                data={mrData?.distribution}
                loading={loadingMr}
                ibPeriods={mrData?.ibPeriods}
                labPeriods={mrData?.labPeriods}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
